import { UzButton } from '@utazzo/ui';
import { ArrowLeft, FileText } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { BookingInfoSection } from '../components/BookingInfoSection';
import { DepositPlan } from '../components/DepositPlan';
import { FlightMulticityGrid } from '../components/FlightMulticityGrid';
import {
  emptyPassengerRow,
  emptySupplierRow,
  isoToDate,
  paiseToRupees,
  rupeesToPaise,
  rupeesToPaiseNum,
  slabUsesBillType,
  toIsoDate,
  type AirlineSectorRow,
  type DepositRow,
  type FareType,
  type GstSlab,
  type MulticityRow,
  type PassengerRow,
  type ProductType,
  type SupplierRow,
  type TrainBusRow,
} from '../components/invoice-form.helpers';
import { PassengerGrid } from '../components/PassengerGrid';
import { PricingSection } from '../components/PricingSection';
import { ProductGstSection } from '../components/ProductGstSection';
import { AirlineSectorGrid, TrainBusGrid } from '../components/SectorGrids';
import { SupplierLinesGrid } from '../components/SupplierLinesGrid';
import {
  useBankAccountOptions,
  useCreateFullInvoice,
  useInvoiceEditData,
  useInvoiceFormOptions,
  useInvoicePreview,
  useUpdateInvoice,
} from '../use-invoices';

import type {
  CreateFullInvoiceRequest,
  CustomerListItem,
  GstPlaceOfSupply,
  InvoicePreviewResponse,
} from '@utazzo/shared';

import { PageShell } from '@/components/page-shell';
import { updateCustomer } from '@/features/customers/customers.api';
import { CustomerPicker } from '@/features/leads/components/CustomerPicker';
import { useBillingEntities } from '@/features/settings/use-settings';

/**
 * Full create-invoice form - a faithful port of the legacy
 * `createinvoicewithgst` 11-section screen, rendered with Uz components. The
 * page owns all draft state; each `UzCard` section is a presentational
 * sub-component. Amounts are held as rupee numbers and converted to BigInt-safe
 * paise strings on submit; dates to ISO. GST is previewed live via a debounced
 * `POST /invoices/preview`.
 */
export const InvoiceCreatePage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const optionsQuery = useInvoiceFormOptions();
  const options = optionsQuery.data;
  const preview = useInvoicePreview();
  const createFull = useCreateFullInvoice();
  const editQuery = useInvoiceEditData(id);
  const editData = editQuery.data;
  const updateInvoice = useUpdateInvoice(id);
  const billingEntitiesQuery = useBillingEntities();
  const billingEntities = billingEntitiesQuery.data;
  const bankAccountsQuery = useBankAccountOptions();
  const bankAccounts = bankAccountsQuery.data;

  // ----- Section 2: Customer -----
  const [customer, setCustomer] = useState<CustomerListItem | null>(null);

  // ----- Section 1: Product / GST -----
  const [productType, setProductType] = useState<ProductType>('DOM');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [gstSlab, setGstSlab] = useState<GstSlab | ''>('');
  // The GST account (billing entity) the invoice is raised under. Defaults to the
  // flagged-default entity once the list loads (see effect below).
  const [billingEntityId, setBillingEntityId] = useState('');
  // Which company bank account prints on the invoice. Defaults to the flagged
  // default once the list loads (see effect below); switchable per invoice.
  const [bankAccountId, setBankAccountId] = useState('');
  // The buyer's GSTIN. Autofilled from the picked customer's profile; mandatory
  // for the corporate (CRP) slab and saved back to the customer on submit.
  const [buyerGstin, setBuyerGstin] = useState('');
  // '' = auto (derive place of supply from the customer's state/country).
  const [placeOfSupply, setPlaceOfSupply] = useState<GstPlaceOfSupply | ''>('');
  const [billType, setBillType] = useState<string>('');
  // Whether the printed invoice shows the CGST/SGST/IGST bifurcation. Defaults on
  // for corporate (CRP), off for retail slabs, until the operator toggles it.
  const [showGstBifurcation, setShowGstBifurcation] = useState(false);
  const bifurcationTouchedRef = useRef(false);
  const [hotelLocationStateId, setHotelLocationStateId] = useState('');
  const [isGroupTour, setIsGroupTour] = useState(false);
  const [groupTourName, setGroupTourName] = useState('');

  // ----- Section 3: Booking info + miscellaneous -----
  const [fareType, setFareType] = useState<FareType | ''>('');
  // City is picked from the master (same picker as the lead form); we keep the
  // selected id for the picker's value and the display label as the text we send.
  const [departure, setDeparture] = useState('');
  const [departureCityId, setDepartureCityId] = useState<string | null>(null);
  const [destination, setDestination] = useState('');
  const [destinationCityId, setDestinationCityId] = useState<string | null>(null);
  const [departureDate, setDepartureDate] = useState<Date | null>(null);
  const [returnDate, setReturnDate] = useState<Date | null>(null);
  const [noOfPax, setNoOfPax] = useState<number | ''>('');
  const [leadGenerationDate, setLeadGenerationDate] = useState<Date | null>(null);
  const [assignedAgentId, setAssignedAgentId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [querySource, setQuerySource] = useState('');
  const [isTravelAgent, setIsTravelAgent] = useState(false);
  const [travelAgentName, setTravelAgentName] = useState('');

  // ----- Sectors + passengers -----
  const [multicityRows, setMulticityRows] = useState<MulticityRow[]>([]);
  const [passengerRows, setPassengerRows] = useState<PassengerRow[]>([emptyPassengerRow()]);
  const [airlineRows, setAirlineRows] = useState<AirlineSectorRow[]>([]);
  const [trainBusRows, setTrainBusRows] = useState<TrainBusRow[]>([]);

  // ----- Section 9: Pricing -----
  const [basic, setBasic] = useState<number | ''>('');
  const [yq, setYq] = useState<number | ''>('');
  const [seatCharge, setSeatCharge] = useState<number | ''>('');
  const [mealCharge, setMealCharge] = useState<number | ''>('');
  const [taxes, setTaxes] = useState<number | ''>('');
  const [discountPercent, setDiscountPercent] = useState<number | ''>('');
  const [saleAmount, setSaleAmount] = useState<number | ''>('');

  // ----- Section 10: Payment -----
  const [paymentMode, setPaymentMode] = useState('');
  const [clientPayingDate, setClientPayingDate] = useState<Date | null>(null);
  const [depositRows, setDepositRows] = useState<DepositRow[]>([]);

  // ----- Section 11: Supplier detail -----
  const [supplierRows, setSupplierRows] = useState<SupplierRow[]>([emptySupplierRow()]);

  const [showErrors, setShowErrors] = useState(false);
  const [previewData, setPreviewData] = useState<InvoicePreviewResponse | null>(null);

  // Edit mode hydrates the whole draft from `editData` exactly once (guarded by
  // this ref) so later re-renders never clobber the operator's in-progress edits.
  const hydratedRef = useRef(false);

  // ----- Derived flags -----
  const soleProduct = selectedProducts.length === 1 ? selectedProducts[0] : null;
  const isFlightOnly = soleProduct === 'FLIGHT';
  const isMultiCity = fareType === 3;
  const isInternational = productType === 'INT';

  const flightBasePaise =
    rupeesToPaiseNum(basic) +
    rupeesToPaiseNum(yq) +
    rupeesToPaiseNum(seatCharge) +
    rupeesToPaiseNum(mealCharge) +
    rupeesToPaiseNum(taxes);
  const discountFraction = discountPercent === '' ? 0 : discountPercent;
  const flightSalePaise = Math.max(
    0,
    Math.round((flightBasePaise * (100 - discountFraction)) / 100),
  );
  const saleAmountPaise = isFlightOnly ? flightSalePaise : rupeesToPaiseNum(saleAmount);

  const totalPayablePaise = useMemo(
    () => supplierRows.reduce((sum, r) => sum + rupeesToPaiseNum(r.payable), 0),
    [supplierRows],
  );

  const grandTotalPaise = previewData ? Number(previewData.grandTotalPaise) : null;

  // Default the GST account to the flagged-default entity once the list loads
  // (and the operator has not already picked one).
  useEffect(() => {
    if (billingEntityId || !billingEntities || billingEntities.length === 0) return;
    const fallback = billingEntities.find((e) => e.isDefault) ?? billingEntities[0];
    if (fallback) setBillingEntityId(fallback.id);
  }, [billingEntities, billingEntityId]);

  // Bank selection is OPTIONAL: leaving it blank prints every active company
  // bank (e.g. ICICI + HDFC) on the invoice; picking one prints only that bank.
  // (No auto-default, so the blank = "show all" default holds.)

  // ----- Validation -----
  const customerMissing = !customer;
  const productsMissing = selectedProducts.length === 0;
  const slabMissing = gstSlab === '';
  const saleMissing = saleAmountPaise <= 0;
  // Corporate invoices must carry the buyer's GSTIN; other slabs may omit it.
  const buyerGstinMissing = gstSlab === 'CRP' && buyerGstin.trim() === '';
  const passengersValid = passengerRows.some((r) => r.firstName.trim() !== '');
  const airlineValid = !isFlightOnly || airlineRows.some((r) => r.paxName.trim() !== '');
  const multicityValid = !isMultiCity || multicityRows.length > 0;
  const anyDeposit = depositRows.some((r) => r.amount !== '' && r.amount !== 0);
  const depositsTotalPaise = depositRows.reduce((sum, r) => sum + rupeesToPaiseNum(r.amount), 0);
  const depositsValid =
    !anyDeposit || (grandTotalPaise !== null && depositsTotalPaise === grandTotalPaise);

  // ----- Live GST preview (debounced) -----
  const billTypeNum = slabUsesBillType(gstSlab) && billType ? Number(billType) : null;
  const previewMutate = preview.mutate;
  useEffect(() => {
    if (gstSlab === '' || saleAmountPaise <= 0) {
      setPreviewData(null);
      return;
    }
    const handle = setTimeout(() => {
      previewMutate(
        {
          ...(customer ? { customerId: customer.id } : {}),
          gstSlab,
          ...(billTypeNum === 1 || billTypeNum === 2 ? { billType: billTypeNum } : {}),
          saleAmountPaise: String(saleAmountPaise),
          supplierPayablePaise: String(totalPayablePaise),
          productType,
          products: selectedProducts,
          ...(placeOfSupply ? { placeOfSupply } : {}),
          ...(billingEntityId ? { billingEntityId } : {}),
          ...(bankAccountId ? { bankAccountId } : {}),
        },
        { onSuccess: (res) => setPreviewData(res) },
      );
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gstSlab,
    billTypeNum,
    saleAmountPaise,
    totalPayablePaise,
    customer?.id,
    productType,
    selectedProducts,
    placeOfSupply,
    billingEntityId,
  ]);

  // ----- Edit mode: hydrate the draft from edit-data (ONCE) -----
  useEffect(() => {
    if (!isEdit || !editData || hydratedRef.current) return;
    // Invoices are editable at any stage; always hydrate the form from edit-data.
    hydratedRef.current = true;

    // Section 2: customer (CustomerListItem-shaped object with the contact
    // details, so the picker tile shows phone + email on edit too).
    setCustomer(
      editData.customer
        ? ({
            id: editData.customer.id,
            displayName: editData.customer.displayName,
            kind: editData.customer.kind,
            email: editData.customer.email ?? null,
            mobileNumber: editData.customer.mobileNumber ?? null,
            companyName: editData.customer.companyName ?? null,
            gstNumber: editData.customer.gstNumber ?? null,
          } as CustomerListItem)
        : null,
    );

    // Section 1: product / GST.
    if (editData.productType) setProductType(editData.productType);
    setSelectedProducts(editData.products.map((p) => p.code));
    setGstSlab(editData.gstSlab);
    setPlaceOfSupply(editData.placeOfSupply);
    setShowGstBifurcation(editData.showGstBifurcation);
    setBillType(editData.billType != null ? String(editData.billType) : '');
    setHotelLocationStateId(editData.hotelLocationStateId ?? '');
    // Restore the saved GST account, chosen bank (blank = all company banks) and
    // buyer GSTIN so the edit form shows what was saved, not the create defaults.
    if (editData.billingEntityId) setBillingEntityId(editData.billingEntityId);
    setBankAccountId(editData.bankAccountId ?? '');
    setBuyerGstin(editData.buyerGstin ?? '');
    setIsGroupTour(editData.isGroupTour);
    setGroupTourName(editData.groupTourName ?? '');

    // Section 3: booking info + miscellaneous.
    setFareType(editData.journeyType ?? '');
    setDeparture(editData.departure ?? '');
    setDepartureCityId(null);
    setDestination(editData.destination ?? '');
    setDestinationCityId(null);
    setDepartureDate(isoToDate(editData.departureDate));
    setReturnDate(isoToDate(editData.returnDate));
    setNoOfPax(editData.noOfPax ?? '');
    setLeadGenerationDate(isoToDate(editData.leadGenerationDate));
    setAssignedAgentId(editData.assignedAgentId ?? '');
    setBranchId(editData.branchId ?? '');
    setQuerySource(editData.querySource ?? '');
    setIsTravelAgent(editData.isTravelAgent);
    setTravelAgentName(editData.travelAgentName ?? '');

    // Sectors.
    setMulticityRows(
      editData.flightMulticities.map((f) => ({
        departureCity: f.departureCity ?? '',
        destinationCity: f.destinationCity ?? '',
        departureDate: isoToDate(f.departureDate),
      })),
    );
    setAirlineRows(
      editData.airlineSectors.map((a) => ({
        paxName: a.paxName,
        eTicket: a.eTicket ?? '',
        pnr: a.pnr ?? '',
        airlineId: a.airlineId ?? '',
        source: a.source ?? '',
        flightType: a.flightType ?? '',
        carrierType: a.carrierType ?? '',
      })),
    );
    setTrainBusRows(
      editData.trainBusSectors.map((t) => ({
        pnr: t.pnr ?? '',
        bookingClass: t.bookingClass ?? '',
        quota: t.quota ?? '',
      })),
    );

    // Passengers (at least one row so the grid never renders empty).
    const pax: PassengerRow[] = editData.passengers.map((p) => ({
      title: p.title ?? '',
      firstName: p.firstName,
      middleName: p.middleName ?? '',
      lastName: p.lastName ?? '',
      paxType: p.paxType,
      dob: isoToDate(p.dob),
      panNo: p.panNo ?? '',
      passportNumber: p.passportNumber ?? '',
      expiryDate: isoToDate(p.expiryDate),
      placeOfIssue: p.placeOfIssue ?? '',
      nationalityCountryId: p.nationalityCountryId ?? '',
    }));
    setPassengerRows(pax.length > 0 ? pax : [emptyPassengerRow()]);

    // Section 9: pricing (paise strings -> rupee numbers; individual flight
    // components are restored so the pricing recomputes identically).
    setBasic(paiseToRupees(editData.basicPaise));
    setYq(paiseToRupees(editData.yqPaise));
    setSeatCharge(paiseToRupees(editData.seatChargePaise));
    setMealCharge(paiseToRupees(editData.mealChargePaise));
    setTaxes(paiseToRupees(editData.taxesPaise));
    setDiscountPercent(editData.discountPercent ? Number(editData.discountPercent) : '');
    setSaleAmount(paiseToRupees(editData.saleAmountPaise));

    // Section 10: payment + deposits.
    setPaymentMode(editData.paymentMode ?? '');
    setClientPayingDate(isoToDate(editData.clientPayingDate));
    setDepositRows(
      editData.deposits.map((d) => ({
        amount: paiseToRupees(d.amountPaise),
        balanceDueDate: isoToDate(d.balanceDueDate),
        isPaid: d.isPaid,
      })),
    );

    // Section 11: supplier detail (at least one row).
    const suppliers: SupplierRow[] = editData.supplierLines.map((s) => ({
      ...(s.id ? { id: s.id } : {}),
      livePaymentCount: s.livePaymentCount ?? 0,
      originalSupplierId: s.supplierId ?? '',
      supplierId: s.supplierId ?? '',
      description: s.description ?? '',
      paymentDueDate: isoToDate(s.paymentDueDate),
      payable: paiseToRupees(s.payableAmountPaise),
      cgst: paiseToRupees(s.cgstPaise),
      sgst: paiseToRupees(s.sgstPaise),
      igst: paiseToRupees(s.igstPaise),
      tds: paiseToRupees(s.tdsPaise),
      currencyId: s.currencyId ?? '',
      exchangeRate: s.exchangeRate ? Number(s.exchangeRate) : '',
      noOfCurrency: s.noOfCurrency ?? '',
    }));
    setSupplierRows(suppliers.length > 0 ? suppliers : [emptySupplierRow()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editData]);

  // ----- Handlers -----
  const handleCustomer = (c: CustomerListItem | null): void => {
    setCustomer(c);
    if (c) {
      // Autofill the buyer GSTIN from the customer's stored profile, if any.
      // `gstNumber` is not on the list-item type, so read it defensively.
      const custGstin = (c as { gstNumber?: string | null }).gstNumber;
      if (custGstin && custGstin.trim()) setBuyerGstin(custGstin.trim());
      setPassengerRows((rows) => {
        if (rows.length > 0 && rows[0] && rows[0].firstName.trim() === '') {
          const parts = c.displayName?.trim().split(/\s+/).filter(Boolean) ?? [];
          const first = parts[0] ?? '';
          const last = parts.slice(1).join(' ');
          return rows.map((r, i) => (i === 0 ? { ...r, firstName: first, lastName: last } : r));
        }
        return rows;
      });
    }
  };

  // Slab drives the bifurcation default (on for CRP, off for retail) until the
  // operator manually toggles the checkbox, after which their choice is kept.
  const handleGstSlab = (v: GstSlab): void => {
    setGstSlab(v);
    if (!bifurcationTouchedRef.current) setShowGstBifurcation(v === 'CRP');
  };

  const handleShowGstBifurcation = (v: boolean): void => {
    bifurcationTouchedRef.current = true;
    setShowGstBifurcation(v);
  };

  const handleProductType = (v: ProductType): void => {
    setProductType(v);
    const allowed = new Set((options?.products[v] ?? []).map((p) => p.code));
    setSelectedProducts((prev) => prev.filter((code) => allowed.has(code)));
  };

  const toggleProduct = (code: string): void =>
    setSelectedProducts((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );

  // ----- Submit -----
  const buildPayload = (): CreateFullInvoiceRequest => {
    const nameByCode = new Map((options?.products[productType] ?? []).map((p) => [p.code, p.name]));
    const products = selectedProducts.map((code) => ({ code, name: nameByCode.get(code) ?? code }));

    const passengers: CreateFullInvoiceRequest['passengers'] = passengerRows
      .filter((r) => r.firstName.trim() !== '')
      .map((r) => ({
        ...(r.title ? { title: r.title } : {}),
        firstName: r.firstName.trim(),
        ...(r.middleName.trim() ? { middleName: r.middleName.trim() } : {}),
        ...(r.lastName.trim() ? { lastName: r.lastName.trim() } : {}),
        paxType: r.paxType,
        ...(toIsoDate(r.dob) ? { dob: toIsoDate(r.dob) } : {}),
        ...(r.panNo.trim() ? { panNo: r.panNo.trim() } : {}),
        ...(isInternational && r.passportNumber.trim()
          ? { passportNumber: r.passportNumber.trim() }
          : {}),
        ...(isInternational && toIsoDate(r.expiryDate)
          ? { expiryDate: toIsoDate(r.expiryDate) }
          : {}),
        ...(isInternational && r.placeOfIssue.trim()
          ? { placeOfIssue: r.placeOfIssue.trim() }
          : {}),
        ...(isInternational && r.nationalityCountryId
          ? { nationalityCountryId: r.nationalityCountryId }
          : {}),
      }));

    const flightMulticities: CreateFullInvoiceRequest['flightMulticities'] = isMultiCity
      ? multicityRows
          .filter((r) => r.departureCity || r.destinationCity || r.departureDate)
          .map((r) => ({
            ...(r.departureCity.trim() ? { departureCity: r.departureCity.trim() } : {}),
            ...(r.destinationCity.trim() ? { destinationCity: r.destinationCity.trim() } : {}),
            ...(toIsoDate(r.departureDate) ? { departureDate: toIsoDate(r.departureDate) } : {}),
          }))
      : [];

    const airlineSectors: CreateFullInvoiceRequest['airlineSectors'] = isFlightOnly
      ? airlineRows
          .filter((r) => r.paxName.trim() !== '')
          .map((r) => ({
            paxName: r.paxName.trim(),
            ...(r.eTicket.trim() ? { eTicket: r.eTicket.trim() } : {}),
            ...(r.pnr.trim() ? { pnr: r.pnr.trim() } : {}),
            ...(r.airlineId ? { airlineId: r.airlineId } : {}),
            ...(r.source.trim() ? { source: r.source.trim() } : {}),
            ...(r.flightType ? { flightType: r.flightType } : {}),
            ...(r.carrierType.trim() ? { carrierType: r.carrierType.trim() } : {}),
          }))
      : [];

    const trainBusSectors: CreateFullInvoiceRequest['trainBusSectors'] =
      soleProduct === 'TRAIN' || soleProduct === 'TRANSFER'
        ? trainBusRows
            .filter((r) => r.pnr || r.bookingClass || r.quota)
            .map((r) => ({
              ...(r.pnr.trim() ? { pnr: r.pnr.trim() } : {}),
              ...(r.bookingClass.trim() ? { bookingClass: r.bookingClass.trim() } : {}),
              ...(soleProduct === 'TRAIN' && r.quota.trim() ? { quota: r.quota.trim() } : {}),
            }))
        : [];

    const deposits: CreateFullInvoiceRequest['deposits'] = depositRows
      .filter((r) => r.amount !== '' && r.amount !== 0)
      .map((r) => ({
        amountPaise: rupeesToPaise(r.amount),
        ...(toIsoDate(r.balanceDueDate) ? { balanceDueDate: toIsoDate(r.balanceDueDate) } : {}),
        isPaid: r.isPaid,
      }));

    const supplierLines: CreateFullInvoiceRequest['supplierLines'] = supplierRows
      .filter((r) => r.supplierId !== '' || r.payable !== '')
      .map((r) => ({
        ...(isEdit && r.id ? { id: r.id } : {}),
        ...(isEdit && r.id && r.supplierChangeReason
          ? { supplierChangeReason: r.supplierChangeReason }
          : {}),
        ...(r.supplierId ? { supplierId: r.supplierId } : {}),
        ...(r.description.trim() ? { description: r.description.trim() } : {}),
        ...(toIsoDate(r.paymentDueDate) ? { paymentDueDate: toIsoDate(r.paymentDueDate) } : {}),
        payableAmountPaise: rupeesToPaise(r.payable),
        ...(rupeesToPaiseNum(r.cgst) > 0 ? { cgstPaise: rupeesToPaise(r.cgst) } : {}),
        ...(rupeesToPaiseNum(r.sgst) > 0 ? { sgstPaise: rupeesToPaise(r.sgst) } : {}),
        ...(rupeesToPaiseNum(r.igst) > 0 ? { igstPaise: rupeesToPaise(r.igst) } : {}),
        ...(rupeesToPaiseNum(r.tds) > 0 ? { tdsPaise: rupeesToPaise(r.tds) } : {}),
        ...(r.currencyId ? { currencyId: r.currencyId } : {}),
        ...(r.exchangeRate !== '' ? { exchangeRate: String(r.exchangeRate) } : {}),
        ...(r.noOfCurrency !== '' ? { noOfCurrency: Math.round(r.noOfCurrency) } : {}),
      }));

    return {
      productType,
      products,
      gstSlab: gstSlab as GstSlab,
      ...(billTypeNum === 1 || billTypeNum === 2 ? { billType: billTypeNum } : {}),
      ...(placeOfSupply ? { placeOfSupply } : {}),
      ...(hotelLocationStateId ? { hotelLocationStateId } : {}),
      showGstBifurcation,
      isCorporate: customer?.kind === 'CORPORATE',
      ...(customer?.companyName && customer.companyName.trim()
        ? { corporateName: customer.companyName.trim() }
        : {}),
      isGroupTour,
      ...(isGroupTour && groupTourName.trim() ? { groupTourName: groupTourName.trim() } : {}),
      ...(billingEntityId ? { billingEntityId } : {}),
      ...(bankAccountId ? { bankAccountId } : {}),
      ...(buyerGstin.trim() ? { buyerGstin: buyerGstin.trim() } : {}),

      customerId: (customer as CustomerListItem).id,

      ...(fareType !== '' ? { journeyType: fareType } : {}),
      ...(!isMultiCity && departure.trim() ? { departure: departure.trim() } : {}),
      ...(!isMultiCity && destination.trim() ? { destination: destination.trim() } : {}),
      ...(!isMultiCity && toIsoDate(departureDate)
        ? { departureDate: toIsoDate(departureDate) }
        : {}),
      ...(!isMultiCity && fareType === 2 && toIsoDate(returnDate)
        ? { returnDate: toIsoDate(returnDate) }
        : {}),
      ...(noOfPax !== '' ? { noOfPax: Math.round(noOfPax) } : {}),

      ...(toIsoDate(leadGenerationDate)
        ? { leadGenerationDate: toIsoDate(leadGenerationDate) }
        : {}),
      ...(assignedAgentId ? { assignedAgentId } : {}),
      ...(branchId ? { branchId } : {}),
      ...(querySource.trim() ? { querySource: querySource.trim() } : {}),
      isTravelAgent,
      ...(isTravelAgent && travelAgentName.trim()
        ? { travelAgentName: travelAgentName.trim() }
        : {}),

      flightMulticities,
      airlineSectors,
      trainBusSectors,
      passengers,

      saleAmountPaise: String(saleAmountPaise),
      ...(isFlightOnly
        ? {
            basicPaise: rupeesToPaise(basic),
            yqPaise: rupeesToPaise(yq),
            seatChargePaise: rupeesToPaise(seatCharge),
            mealChargePaise: rupeesToPaise(mealCharge),
            taxesPaise: rupeesToPaise(taxes),
            ...(discountPercent !== '' ? { discountPercent: String(discountPercent) } : {}),
          }
        : {}),

      ...(paymentMode ? { paymentMode } : {}),
      ...(toIsoDate(clientPayingDate) ? { clientPayingDate: toIsoDate(clientPayingDate) } : {}),
      deposits,
      supplierLines,
    };
  };

  // Best-effort: persist the entered GSTIN back onto the customer profile when it
  // is new or changed, so a corporate buyer only has to be typed once. A failure
  // here must not break the invoice flow - warn and carry on.
  const persistBuyerGstin = (): void => {
    if (gstSlab !== 'CRP' || !customer) return;
    const entered = buyerGstin.trim();
    if (!entered) return;
    const existing = (customer as { gstNumber?: string | null }).gstNumber?.trim() ?? '';
    if (entered === existing) return;
    void updateCustomer(customer.id, { gstNumber: entered }).catch(() => {
      toast.warning('Invoice saved, but the buyer GSTIN could not be saved to the customer.');
    });
  };

  const submit = (): void => {
    if (
      customerMissing ||
      productsMissing ||
      slabMissing ||
      saleMissing ||
      buyerGstinMissing ||
      !passengersValid ||
      !airlineValid ||
      !multicityValid ||
      !depositsValid
    ) {
      setShowErrors(true);
      if (anyDeposit && !depositsValid) {
        toast.error('Deposits total must equal the grand total.');
      } else {
        toast.error(
          isEdit
            ? 'Fix the highlighted fields before saving the invoice.'
            : 'Fix the highlighted fields before creating the invoice.',
        );
      }
      return;
    }

    const payload = buildPayload();
    if (isEdit && id) {
      updateInvoice.mutate(payload, {
        onSuccess: (res) => {
          persistBuyerGstin();
          if ((res.pendingSupplierChanges ?? 0) > 0) {
            toast.info('Supplier change(s) sent for admin approval');
          }
          navigate(`/invoices/${id}`);
        },
      });
    } else {
      createFull.mutate(payload, {
        onSuccess: (res) => {
          persistBuyerGstin();
          navigate(`/invoices/${res.id}`);
        },
      });
    }
  };

  // Edit mode: while the draft loads, or when the invoice is not editable, show
  // a small notice instead of the (unhydrated) form.
  if (isEdit && (editQuery.isLoading || !editData)) {
    return (
      <PageShell title="Edit invoice" description="Loading the invoice.">
        <p className="text-sm text-zinc-500">Loading invoice...</p>
      </PageShell>
    );
  }
  return (
    <PageShell
      title={isEdit ? 'Edit invoice' : 'Create invoice'}
      description={
        isEdit
          ? 'Edit this proforma invoice - the full legacy create-invoice form.'
          : 'Raise a GST invoice directly - the full legacy create-invoice form.'
      }
      actions={
        <UzButton
          variant="outline"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate(isEdit ? `/invoices/${id}` : '/invoices')}
        >
          {isEdit ? 'Back to invoice' : 'Back to invoices'}
        </UzButton>
      }
    >
      <div className="max-w-5xl space-y-6">
        <ProductGstSection
          options={options}
          productType={productType}
          onProductType={handleProductType}
          selectedProducts={selectedProducts}
          onToggleProduct={toggleProduct}
          gstSlab={gstSlab}
          onGstSlab={handleGstSlab}
          placeOfSupply={placeOfSupply}
          onPlaceOfSupply={setPlaceOfSupply}
          showGstBifurcation={showGstBifurcation}
          onShowGstBifurcation={handleShowGstBifurcation}
          billType={billType}
          onBillType={setBillType}
          hotelLocationStateId={hotelLocationStateId}
          onHotelLocationStateId={setHotelLocationStateId}
          isGroupTour={isGroupTour}
          onIsGroupTour={setIsGroupTour}
          groupTourName={groupTourName}
          onGroupTourName={setGroupTourName}
          billingEntities={billingEntities}
          billingEntityId={billingEntityId}
          onBillingEntityId={setBillingEntityId}
          bankAccounts={bankAccounts}
          bankAccountId={bankAccountId}
          onBankAccountId={setBankAccountId}
          buyerGstin={buyerGstin}
          onBuyerGstin={setBuyerGstin}
          buyerGstinRequired={gstSlab === 'CRP'}
          buyerGstinMissing={buyerGstinMissing}
          showErrors={showErrors}
          slabMissing={slabMissing}
          productsMissing={productsMissing}
        />

        <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-lg font-semibold text-zinc-900">Customer</h3>
          <CustomerPicker
            value={customer}
            onChange={handleCustomer}
            required
            kind={gstSlab === 'CRP' ? 'CORPORATE' : undefined}
            error={showErrors && customerMissing ? 'Pick a customer' : undefined}
          />
          {customer && (
            <p className="mt-2 text-xs text-zinc-500">
              {customer.displayName}
              {customer.companyName ? ` - ${customer.companyName}` : ''}
              {customer.mobileNumber ? ` - ${customer.mobileNumber}` : ''}
              {customer.email ? ` - ${customer.email}` : ''}
            </p>
          )}
        </div>

        <BookingInfoSection
          options={options}
          fareType={fareType}
          onFareType={setFareType}
          isMultiCity={isMultiCity}
          departureCityId={departureCityId}
          departureCityLabel={departure || null}
          onDepartureCity={({ id, label }) => {
            setDepartureCityId(id);
            setDeparture(label ?? '');
          }}
          destinationCityId={destinationCityId}
          destinationCityLabel={destination || null}
          onDestinationCity={({ id, label }) => {
            setDestinationCityId(id);
            setDestination(label ?? '');
          }}
          departureDate={departureDate}
          onDepartureDate={setDepartureDate}
          returnDate={returnDate}
          onReturnDate={setReturnDate}
          noOfPax={noOfPax}
          onNoOfPax={setNoOfPax}
          leadGenerationDate={leadGenerationDate}
          onLeadGenerationDate={setLeadGenerationDate}
          assignedAgentId={assignedAgentId}
          onAssignedAgentId={setAssignedAgentId}
          branchId={branchId}
          onBranchId={setBranchId}
          querySource={querySource}
          onQuerySource={setQuerySource}
          isTravelAgent={isTravelAgent}
          onIsTravelAgent={setIsTravelAgent}
          travelAgentName={travelAgentName}
          onTravelAgentName={setTravelAgentName}
        />

        {isMultiCity && (
          <FlightMulticityGrid
            rows={multicityRows}
            onChange={setMulticityRows}
            showErrors={showErrors}
            invalid={!multicityValid}
          />
        )}

        <PassengerGrid
          options={options}
          rows={passengerRows}
          onChange={setPassengerRows}
          isInternational={isInternational}
          showErrors={showErrors}
          invalid={!passengersValid}
        />

        {(soleProduct === 'TRAIN' || soleProduct === 'TRANSFER') && (
          <TrainBusGrid mode={soleProduct} rows={trainBusRows} onChange={setTrainBusRows} />
        )}

        {isFlightOnly && (
          <AirlineSectorGrid
            rows={airlineRows}
            onChange={setAirlineRows}
            showErrors={showErrors}
            invalid={!airlineValid}
          />
        )}

        <PricingSection
          gstSlab={gstSlab}
          isFlightOnly={isFlightOnly}
          basic={basic}
          onBasic={setBasic}
          yq={yq}
          onYq={setYq}
          seatCharge={seatCharge}
          onSeatCharge={setSeatCharge}
          mealCharge={mealCharge}
          onMealCharge={setMealCharge}
          taxes={taxes}
          onTaxes={setTaxes}
          discountPercent={discountPercent}
          onDiscountPercent={setDiscountPercent}
          saleAmount={saleAmount}
          onSaleAmount={setSaleAmount}
          saleAmountPaise={saleAmountPaise}
          preview={previewData}
          previewLoading={preview.isPending}
          showErrors={showErrors}
          saleMissing={saleMissing}
        />

        <DepositPlan
          options={options}
          paymentMode={paymentMode}
          onPaymentMode={setPaymentMode}
          clientPayingDate={clientPayingDate}
          onClientPayingDate={setClientPayingDate}
          rows={depositRows}
          onChange={setDepositRows}
          grandTotalPaise={grandTotalPaise}
        />

        <SupplierLinesGrid rows={supplierRows} onChange={setSupplierRows} />

        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" asChild>
            <Link to={isEdit ? `/invoices/${id}` : '/invoices'}>Cancel</Link>
          </UzButton>
          <UzButton
            leftIcon={<FileText className="h-4 w-4" />}
            loading={isEdit ? updateInvoice.isPending : createFull.isPending}
            onClick={submit}
          >
            {isEdit ? 'Save changes' : 'Create invoice'}
          </UzButton>
        </div>
      </div>
    </PageShell>
  );
};
